import {
  Badge,
  Button,
  Card,
  CardTitle,
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/ui";
import { 
  ClipboardList,
  FileText,
  Mic,
  Briefcase,
  ArrowRight,
  Stethoscope,
  Heart,
  Clock
} from "lucide-react";
import { useLocation } from "wouter";
import { useState, useEffect } from "react";
const cliniciansImage = "/assets/website/images/Clinicians_using_AI_tools_b789fe10.png";
const clinicianTriageImage = "/assets/website/images/clinician-triage.jpg";
const clinicianTriageTwoImage = "/assets/website/images/Clinician-Triage-two.jpg";
const ambientOneImage = "/assets/website/images/ambientone.jpg";
const ambientTwoImage = "/assets/website/images/ambienttwo.jpg";
const chartprepOneImage = "/assets/website/images/chartprepone.jpg";
const chartprepTwoImage = "/assets/website/images/Charpretwo.jpg";
const occuOneImage = "/assets/website/images/occuone.jpg";
const occuTwoImage = "/assets/website/images/occutwo.jpg";

const urgentCareSolutions = [
  {
    icon: ClipboardList,
    title: "Clinical Intake & Triage",
    description: "Intelligent patient assessment and priority-based care routing",
    longDescription: "Transform your clinical intake process with AI-powered triage that intelligently assesses patient symptoms, prioritizes care needs, and routes patients to the most appropriate care level. Our system combines clinical expertise with advanced algorithms to ensure optimal patient outcomes and efficient resource utilization.",
    images: [
      clinicianTriageImage,
      clinicianTriageTwoImage,
    ],
    features: [
      "AI-powered triage with clinical decision support",
      "Comprehensive symptom assessment and documentation",
      "Intelligent priority queue management",
      "Real-time clinical decision support alerts",
      "Context-aware Chatbots to aid clinicians",
      "Automated workflow routing"
    ],
    kpis: [
      { label: "Triage Accuracy", value: "94%" },
      { label: "Wait Time Reduction", value: "-45%" },
      { label: "Patient Throughput", value: "+35%" }
    ],
    additionalText: "Our Clinical Intake & Triage solution leverages advanced AI algorithms to analyze patient presentations, medical history, and vital signs, providing clinicians with evidence-based recommendations for optimal care pathways."
  },
  {
    icon: FileText,
    title: "Chartprep Automation",
    description: "AI-powered chart preparation for urgent care episodic visits",
    longDescription: "Streamline your documentation workflow with intelligent chart preparation that automatically gathers patient information, reviews medical history, and pre-populates encounter documentation. Save valuable time while ensuring comprehensive and accurate clinical records.",
    images: [
      chartprepOneImage,
      chartprepTwoImage,
    ],
    features: [
      "Automated chart preparation and pre-visit documentation",
      "Intelligent patient history review and summarization",
      "Pre-populated clinical templates based on visit type",
      "Real-time clinical decision support integration",
      "Automated medication reconciliation",
      "Allergy and adverse reaction alerts"
    ],
    kpis: [
      { label: "Documentation Time", value: "-50%" },
      { label: "Chart Completeness", value: "95%" },
      { label: "Provider Satisfaction", value: "92%" }
    ],
    additionalText: "Episodic Chartprep Automation transforms the way clinicians prepare for patient visits, automatically gathering and organizing relevant clinical information to support efficient, high-quality care delivery."
  },
  {
    icon: Mic,
    title: "Ambient Assistance",
    description: "Real-time voice transcription and clinical documentation during patient visits",
    longDescription: "Revolutionize clinical documentation with ambient AI that captures natural conversation between clinicians and patients, automatically generating accurate clinical notes in real-time. Reduce documentation burden and allow providers to focus on what matters most—patient care.",
    images: [
      ambientOneImage,
      ambientTwoImage,
    ],
    features: [
      "Real-time voice transcription with high accuracy",
      "Automated clinical documentation generation",
      "Natural conversation capture without interruption",
      "Accurate Clinitcal Code Prediction",
      "Multi-speaker recognition and attribution",
      "HIPAA-compliant secure processing"
    ],
    kpis: [
      { label: "Documentation Time", value: "-50%" },
      { label: "Accuracy", value: "98%" },
      { label: "Provider Satisfaction", value: "96%" }
    ],
    additionalText: "Ambient Assistance uses advanced natural language processing and machine learning to understand clinical context, automatically generating structured clinical documentation that captures the full patient encounter narrative."
  },
  {
    icon: Briefcase,
    title: "Occupational Medical Service",
    description: "Comprehensive occupational health services for employee wellness and workplace compliance",
    longDescription: "Deliver comprehensive occupational health services with our integrated platform that streamlines employee health screenings, drug testing, immunizations, and compliance management. Support workplace safety, regulatory compliance, and employee wellness programs with automated workflows and intelligent documentation.",
    images: [
      occuOneImage,
      occuTwoImage,
    ],
    features: [
      "Comprehensive employee health screenings and assessments",
      "Drug and alcohol testing with chain-of-custody management",
      "Immunization tracking and compliance monitoring",
      "DOT and OSHA compliance documentation",
      "Workplace injury evaluation and case management",
      "Automated reporting for employers and regulatory bodies",
      "Dedicated employer portal to manage OCC medical Service"
    ],
    kpis: [
      { label: "Compliance Rate", value: "99.8%" },
      { label: "Processing Time", value: "-60%" },
      { label: "Client Satisfaction", value: "94%" }
    ],
    additionalText: "Our Occupational Medical Service platform ensures seamless coordination between employers, employees, and healthcare providers, delivering efficient, compliant occupational health services that protect both workers and organizations."
  }
];

const workflowBenefits = [
  {
    icon: Clock,
    title: "Time Savings",
    description: "Streamlined workflows reduce administrative time",
    improvement: "60% reduction in check-in time"
  },
  {
    icon: Heart,
    title: "Patient Satisfaction",
    description: "Improved patient experience and engagement",
    improvement: "90% patient satisfaction score"
  },
  {
    icon: Stethoscope,
    title: "Operational Efficiency",
    description: "Enhanced care delivery and resource optimization",
    improvement: "45% reduction in wait times"
  }
];

export default function CliniciansUrgentCare() {
  const [, setLocation] = useLocation();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const handleLearnMore = () => {
    setLocation('/contact');
  };

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div>
      <main>
        <section id="clinicians-urgent-care" className="py-20">
      {/* Hero Image Section */}
      <div className="relative h-80 mb-20 overflow-hidden">
        <img 
          src={cliniciansImage} 
          alt="Clinicians Using AI Tools for Urgent Care"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Badge variant="secondary" className="mb-4" data-testid="badge-clinicians-urgent-care">
              Solutions for Urgent Care Clinicians
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-testid="text-clinicians-urgent-care-title">
              Streamlined Urgent Care Operations
            </h2>
            <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-clinicians-urgent-care-description">
              Transform your urgent care facility with AI-powered workflows that enhance patient experience, 
              streamline operations, and ensure optimal care delivery.
            </p>
          </div>
        </div>
      </div>
      
      <div className="w-full px-4 py-16">
        {/* Solutions Carousel */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="text-solutions-title">
              Urgent Care Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our comprehensive AI-powered solutions designed specifically for urgent care clinicians
            </p>
          </div>

          <div className="relative">
            <Carousel
              setApi={setApi}
              className="w-full max-w-[90%] mx-auto"
              opts={{
                align: "start",
                loop: true,
              }}
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {urgentCareSolutions.map((solution, index) => (
                  <CarouselItem key={index} className="pl-2 md:pl-4 basis-full">
                    <Card className="hover-elevate shadow-lg border border-gray-300" data-testid={`card-urgent-care-solution-${index}`}>
                      <div className="grid md:grid-cols-[3fr_1fr] gap-5 p-5 md:p-7 items-start">
                        {/* Left Column - Image Section */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-lg">
                              <solution.icon className="h-7 w-7 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-2xl md:text-3xl" data-testid={`text-solution-title-${index}`}>
                                {solution.title}
                              </CardTitle>
                            </div>
                          </div>
                          
                          {/* Image Gallery - Carousel for sliding images */}
                          <div className="relative">
                            {solution.images && solution.images.length > 0 ? (
                              <Carousel
                                className="w-full"
                                opts={{
                                  align: "start",
                                  loop: solution.images.length > 1,
                                }}
                              >
                                <CarouselContent className="-ml-0">
                                  {solution.images.map((image, imgIndex) => (
                                    <CarouselItem key={imgIndex} className="pl-0 basis-full">
                                      <div className="relative w-full rounded-lg overflow-hidden bg-muted">
                                        <img 
                                          src={image} 
                                          alt={`${solution.title} - Image ${imgIndex + 1}`}
                                          className="w-full h-auto object-cover"
                                        />
                                      </div>
                                    </CarouselItem>
                                  ))}
                                </CarouselContent>
                                {solution.images.length > 1 && (
                                  <>
                                    <CarouselPrevious className="left-2 h-10 w-10 rounded-full bg-primary/90 text-primary-foreground hover:bg-primary border border-primary shadow-lg z-20" />
                                    <CarouselNext className="right-2 h-10 w-10 rounded-full bg-primary/90 text-primary-foreground hover:bg-primary border border-primary shadow-lg z-20" />
                                  </>
                                )}
                              </Carousel>
                            ) : (
                              <div className="relative w-full rounded-lg overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-dashed border-primary/20 flex items-center justify-center min-h-[300px]">
                                <div className="text-center p-6">
                                  <solution.icon className="h-12 w-12 text-primary/40 mx-auto mb-3" />
                                  <p className="text-sm text-muted-foreground">
                                    Add your images here
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Update the images array in urgentCareSolutions
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right Column - Content Section */}
                        <div className="space-y-6 self-start">
                          <div>
                            <p className="text-base text-foreground/90 mb-4 leading-relaxed" data-testid={`text-solution-description-${index}`}>
                              {solution.longDescription}
                            </p>
                            {solution.additionalText && (
                              <p className="text-sm text-foreground/80 leading-relaxed italic">
                                {solution.additionalText}
                              </p>
                            )}
                          </div>

                          {/* Features */}
                          <div>
                            <h4 className="font-semibold mb-4 text-lg">Key Features</h4>
                            <ul className="space-y-3">
                              {solution.features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="flex items-start text-sm text-foreground/90" data-testid={`text-feature-${index}-${featureIndex}`}>
                                  <div className="w-2 h-2 bg-primary rounded-full mr-3 mt-2 flex-shrink-0" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* KPIs */}
                          <div>
                            <h4 className="font-semibold mb-4 text-lg">
                              Impact Metrics<span className="text-red-500">*</span>
                            </h4>
                            <div className="grid grid-cols-3 gap-4">
                              {solution.kpis.map((kpi, kpiIndex) => (
                                <div key={kpiIndex} className="text-center p-4 bg-muted/50 rounded-lg border border-primary/10" data-testid={`kpi-${index}-${kpiIndex}`}>
                                  <div className="text-2xl font-bold text-primary mb-1" data-testid={`kpi-value-${index}-${kpiIndex}`}>
                                    {kpi.value}
                                  </div>
                                  <div className="text-xs text-foreground/80" data-testid={`kpi-label-${index}-${kpiIndex}`}>
                                    {kpi.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {urgentCareSolutions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={`h-3 rounded-full transition-all ${
                    current === index
                      ? "w-8 bg-primary"
                      : "w-3 bg-muted-foreground/30"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Workflow Benefits */}
        <div className="bg-background rounded-lg p-8 mb-12 max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-benefits-title">
            Workflow Transformation Benefits
          </h3>
          <div className="grid md:grid-cols-3 gap-6 place-items-center justify-items-center">
            {workflowBenefits.map((benefit, index) => (
              <div key={index} className="text-center" data-testid={`benefit-${index}`}>
                <div className="flex items-center justify-center w-16 h-16 bg-accent/20 rounded-lg mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-accent-foreground" />
                </div>
                <h4 className="font-semibold text-foreground mb-2" data-testid={`benefit-title-${index}`}>
                  {benefit.title}
                </h4>
                <p className="text-sm text-muted-foreground mb-3" data-testid={`benefit-description-${index}`}>
                  {benefit.description}
                </p>
                <Badge variant="secondary" className="text-xs" data-testid={`benefit-improvement-${index}`}>
                  {benefit.improvement}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Transform Your Urgent Care Operations?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Discover how our AI-powered urgent care solutions can streamline your workflows, 
              improve patient satisfaction, and optimize your facility's performance.
            </p>
            <Button 
              onClick={handleLearnMore}
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Learn More About Urgent Care Solutions
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

      </div>
    </section>
      </main>
    </div>
  );
}

